import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AdminOrganizationRow } from '../../../domain/superadmin/types';
import { PageHeader } from '../../../components/layout/Header';
import { Button } from '../../../components/ui';
import { AppIcon } from '../../../components/icons/AppIcon';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert';
import Spinner from '../../../components/ui/Spinner';
import Pagination from '../../../components/shared/Pagination';
import { useAppContext } from '../../../contexts/AppContext';
import { useAdminOrganizations } from '../hooks/useAdminOrganizations';
import { OrganizationsFilters } from './OrganizationsFilters';
import { OrganizationsTable } from './OrganizationsTable';
import { ImpersonationStartDialog } from './ImpersonationStartDialog';

const DEBOUNCE_MS = 300;

export function AdminOrganizationsPage() {
    const { openSuperadminOrg } = useAppContext();
    const {
        data,
        isLoading,
        error,
        refetch,
        page,
        setPage,
        setQ,
        filters,
        patchFilters,
        pageSize,
    } = useAdminOrganizations();

    const [searchInput, setSearchInput] = useState('');
    const [impersonateTarget, setImpersonateTarget] = useState<AdminOrganizationRow | null>(null);

    useEffect(() => {
        const timer = setTimeout(() => {
            setQ(searchInput);
            setPage(1);
        }, DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchInput, setQ, setPage]);

    const total = data?.total ?? 0;

    const headerAction = useMemo(
        () => (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-s3 py-1.5 text-[12px] text-secondary">
                <AppIcon name="building" className="size-3.5 text-primary" />
                <span className="font-medium tabular-nums text-default">{total}</span>
                <span className="text-muted">{total === 1 ? 'empresa' : 'empresas'}</span>
            </span>
        ),
        [total],
    );

    const handleImpersonate = useCallback((org: AdminOrganizationRow) => {
        setImpersonateTarget(org);
    }, []);

    return (
        <div className="flex flex-col bg-background">
            <PageHeader
                title="Empresas"
                subtitle="Consulte cadastros, planos e libere ou bloqueie acessos."
                titleClassName="text-xl font-semibold tracking-tight text-default"
                subtitleClassName="text-[13px] text-muted"
                className="mb-[18px]"
                hideBackButton
                action={headerAction}
            />

            <OrganizationsFilters
                filters={filters}
                search={searchInput}
                onSearch={setSearchInput}
                onChange={patchFilters}
            />

            {isLoading && (
                <div className="flex items-center justify-center py-s16" role="status" aria-live="polite">
                    <Spinner color="primary" size="lg" />
                </div>
            )}

            {!isLoading && error && (
                <Alert variant="destructive">
                    <AlertTitle>Erro ao carregar</AlertTitle>
                    <AlertDescription className="flex flex-col gap-s3">
                        <p>{error}</p>
                        <Button variant="outline" size="sm" onClick={() => void refetch()}>
                            Tentar novamente
                        </Button>
                    </AlertDescription>
                </Alert>
            )}

            {!isLoading && !error && data && (
                <>
                    <OrganizationsTable
                        items={data.items}
                        onOpen={openSuperadminOrg}
                        onImpersonate={handleImpersonate}
                    />
                    {total > 0 && (
                        <Pagination
                            className="mt-s4 shrink-0"
                            currentPage={page}
                            totalCount={total}
                            pageSize={pageSize}
                            onPageChange={setPage}
                        />
                    )}
                </>
            )}
            <ImpersonationStartDialog
                open={impersonateTarget !== null}
                org={impersonateTarget ? { id: impersonateTarget.id, name: impersonateTarget.name } : null}
                onClose={() => setImpersonateTarget(null)}
            />
        </div>
    );
}
